import { useCallback, useEffect, memo, useRef, useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { AlertType } from '../types';
import { useAlerts } from '../hooks/useAlerts';
import { AlertCard } from './AlertCard';
import { useNavigate } from 'react-router-dom';
import { useAlertMetrics } from '@/hooks/useAlertMetrics';

interface AlertListProps {
  type: AlertType;
  onCountChange?: (count: number) => void;
}

const ANALOG_THRESHOLD = 0.5; // Threshold for analog stick movement
const GAMEPAD_POLL_RATE = 100; // How often to check gamepad state in ms

export const AlertList = memo(({ type, onCountChange }: AlertListProps) => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useAlerts(
    type,
    onCountChange,
    'v1'
  );
  const [selectedAlertIndex, setSelectedAlertIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const { trackAlertSelection } = useAlertMetrics();

  const alerts = useMemo(() => data?.pages.flatMap((page) => page.alerts) || [], [data?.pages]);

  const navigateToAlert = useCallback(
    (alertId: string | undefined) => {
      if (alertId) {
        const alert = alerts.find((a) => a.id === alertId);
        trackAlertSelection(alertId);
        navigate(`/alert/${alertId}`);
      }
    },
    [navigate, trackAlertSelection, alerts]
  );

  useEffect(() => {
    setSelectedAlertIndex(0);
  }, [type]);

  const handleInput = useCallback(
    (event?: KeyboardEvent) => {
      // Skip if user is typing in an input field
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      // Get number of columns based on current window width
      let numColumns = 1;
      if (window.innerWidth >= 1536) {
        // 2xl
        numColumns = 3;
      } else if (window.innerWidth >= 768) {
        // md
        numColumns = 2;
      }

      // If this is a keyboard event, get the key
      const key = event?.key;

      // If this is a gamepad input, determine which direction
      const gamepad = navigator.getGamepads?.()?.[0];
      const dpadUp = gamepad?.buttons[12]?.pressed;
      const dpadDown = gamepad?.buttons[13]?.pressed;
      const dpadLeft = gamepad?.buttons[14]?.pressed;
      const dpadRight = gamepad?.buttons[15]?.pressed;
      const analogY = gamepad?.axes[1] || 0;
      const analogX = gamepad?.axes[0] || 0;

      // Determine which direction to move based on input
      const isUp = key === 'ArrowUp' || dpadUp || analogY < -ANALOG_THRESHOLD;
      const isDown = key === 'ArrowDown' || dpadDown || analogY > ANALOG_THRESHOLD;
      const isLeft = key === 'ArrowLeft' || dpadLeft || analogX < -ANALOG_THRESHOLD;
      const isRight = key === 'ArrowRight' || dpadRight || analogX > ANALOG_THRESHOLD;
      const isEnter = key === 'Enter' || gamepad?.buttons[0]?.pressed; // A button

      if (isUp) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          const newIndex = prevIndex - numColumns;
          return newIndex >= 0 ? newIndex : prevIndex;
        });
      } else if (isDown) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          const newIndex = prevIndex + numColumns;
          return newIndex < alerts.length ? newIndex : prevIndex;
        });
      } else if (isLeft) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          return prevIndex > 0 ? prevIndex - 1 : prevIndex;
        });
      } else if (isRight) {
        event?.preventDefault?.();
        setSelectedAlertIndex((prevIndex) => {
          if (prevIndex === null) return 0;
          return prevIndex < alerts.length - 1 ? prevIndex + 1 : prevIndex;
        });
      } else if (isEnter) {
        event?.preventDefault?.();
        if (selectedAlertIndex !== null && alerts[selectedAlertIndex]) {
          navigateToAlert(alerts[selectedAlertIndex].id);
        }
      }
    },
    [selectedAlertIndex, alerts, navigateToAlert]
  );

  // Handle keyboard events
  useEffect(() => {
    window.addEventListener('keydown', handleInput);
    return () => {
      window.removeEventListener('keydown', handleInput);
    };
  }, [handleInput]);

  // Poll gamepad state
  useEffect(() => {
    const interval = setInterval(() => {
      handleInput();
    }, GAMEPAD_POLL_RATE);

    return () => {
      clearInterval(interval);
    };
  }, [handleInput]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        // Only load more when user has scrolled down enough
        rootMargin: '200px 0px',
      }
    );

    const currentLoaderRef = loaderRef.current;

    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (selectedAlertIndex !== null) {
      const selectedElement = document.querySelector(`[data-alert-index="${selectedAlertIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [selectedAlertIndex]);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-[#B7D257]">
        <Loader2 className="mx-auto animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative">
      {isFetchingNextPage && !isLoading && (
        <div className="absolute right-4 top-4">
          <Loader2 className="animate-spin text-white" size={24} />
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 p-4 max-2xl:grid-cols-2 max-md:grid-cols-1">
        {alerts.map((alert, index) => (
          <AlertCard
            key={alert.id || index}
            alert={alert}
            onTakeAlert={navigateToAlert}
            isTaking={false}
            type={type}
            isSelected={selectedAlertIndex === index}
            data-alert-index={index}
          />
        ))}
      </div>

      {hasNextPage && (
        <div ref={loaderRef} className="flex justify-center py-4">
          {isFetchingNextPage && <Loader2 className="animate-spin text-[#B7D257]" />}
        </div>
      )}

      {!hasNextPage && alerts.length > 0 && (
        <p className="py-4 text-center text-sm text-gray-500">All&nbsp;alerts loaded</p>
      )}

      {!hasNextPage && alerts.length === 0 && (
        <div className="flex h-[calc(100vh-400px)] flex-col items-center justify-center">
          <img src="/icon/logo.svg" alt="Logo" className="h-16 w-16 opacity-50" />
          <p className="mb-1 text-xl font-semibold text-gray-50">No alerts</p>
          <p>Nice work! You're all caught up</p>
        </div>
      )}
    </div>
  );
});
